/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
import { RefObject, useEffect, useState } from 'react';

/**
 * Decides whether a list's pager should float (dock above the input).
 *
 * Rule: float only while the list itself is still in view but its inline pager
 * is not — i.e. the user is reading a long list whose Prev/Next has scrolled
 * past the fold. A list scrolled fully out of view never floats.
 *
 * `root` is the scroll container the chat lives in (the IntersectionObserver
 * root); the window is not the scroller.
 */
export function useFloatingPager(
  listRef: RefObject<HTMLElement>,
  pagerRef: RefObject<HTMLElement>,
  root: HTMLElement | null,
  enabled: boolean
): boolean {
  const [listVisible, setListVisible] = useState(false);
  const [pagerVisible, setPagerVisible] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    const list = listRef.current;
    const pager = pagerRef.current;
    if (!list || !pager) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === list) setListVisible(entry.isIntersecting);
          if (entry.target === pager) setPagerVisible(entry.isIntersecting);
        }
      },
      { root: root ?? null, threshold: 0 }
    );

    observer.observe(list);
    observer.observe(pager);
    return () => observer.disconnect();
  }, [enabled, root, listRef, pagerRef]);

  return enabled && listVisible && !pagerVisible;
}
